import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi, departmentsApi, locationsApi, dashboardApi } from '../lib/api';
import OrgTree, { getManagerIds, buildOrgTree, LENS } from '../components/OrgTree';
import AddEditEmployeeDialog from '../components/AddEditEmployeeDialog';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar, Clock, CheckSquare, Users, Building2, BarChart3, 
  Settings, Menu, X, LogOut, ChevronDown, ChevronRight, Search,
  MapPin, CalendarDays, Shield, GitBranch,
  Eye, Edit, User, Mail, Phone, ChevronsUpDown, ChevronsDownUp
} from 'lucide-react';
import { roleLabel, roleBadgeClass, roleAvatarClass } from '../lib/roleBadges';

const OrgChartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsEnabled, setLocationsEnabled] = useState(false);
  const viewMode = 'chart'; // the table view was removed 30/07/2026 — see the note below
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [collapsedIds, setCollapsedIds] = useState(new Set());
  // Approvals leads by design — see the two-lens spec in DESIGN-org-and-approvals.md.
  const [lens, setLens] = useState(LENS.APPROVALS);
  // Clicking anyone in the chart opens THE SAME dialog as Employees -> click a name.
  // Deliberately not a bespoke panel: one screen, so every future change to it shows up
  // in both places. (Rob, 30/07: "please don't reinvent the wheel".)
  const [editing, setEditing] = useState(null);          // the employee being edited
  const [editHighlight, setEditHighlight] = useState(false); // arrived from the strip?
  const didInitCollapse = useRef(false);
  useEffect(() => {
    // Default: show the top two levels, collapse deeper subtrees.
    if (!didInitCollapse.current && employees.length) {
      const { topLeader } = buildOrgTree(employees, departments);
      const deep = getManagerIds(employees, departments).filter((id) => !topLeader || id !== topLeader.id);
      setCollapsedIds(new Set(deep));
      didInitCollapse.current = true;
    }
  }, [employees, departments]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approverChains, setApproverChains] = useState({});
  const [openStat, setOpenStat] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, deptsRes, locationSettingsRes, locationsRes, chainsRes] = await Promise.all([
        usersApi.list(),
        departmentsApi.list(),
        locationsApi.getSettings().catch(() => ({ data: { multi_location_enabled: false } })),
        locationsApi.list().catch(() => ({ data: [] })),
        dashboardApi.getApproverChains().catch(() => ({ data: {} }))
      ]);
      
      setEmployees(usersRes.data || []);
      setDepartments(deptsRes.data || []);
      setLocationsEnabled(locationSettingsRes.data.multi_location_enabled);
      setLocations(locationsRes.data || []);
      setApproverChains(chainsRes.data || {});
      
      // Expand all nodes by default
      const allDeptIds = new Set(deptsRes.data?.map(d => `dept-${d.id}`) || []);
      setExpandedNodes(allDeptIds);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load organisation data');
    } finally {
      setLoading(false);
    }
  };

  // Build hierarchical structure for org chart
  const orgStructure = useMemo(() => {
    // For departments that are NOT shared across locations, we need to split them by location
    const deptNodes = [];
    
    departments.forEach(dept => {
      if (dept.is_shared_across_locations === false && locationsEnabled && locations.length > 0) {
        // Split this department by location
        locations.forEach(location => {
          const locationEmployees = employees.filter(
            emp => emp.department_id === dept.id && emp.location_id === location.id
          );
          
          if (locationEmployees.length > 0) {
            const manager = locationEmployees.find(emp => emp.id === dept.manager_id && emp.location_id === location.id);
            const nonManagers = locationEmployees.filter(emp => emp.id !== dept.manager_id);
            
            deptNodes.push({
              ...dept,
              id: `${dept.id}-${location.id}`, // Composite ID for split departments
              originalDeptId: dept.id,
              name: `${dept.name} (${location.name})`,
              locationId: location.id,
              locationName: location.name,
              isSplit: true,
              manager: manager || null,
              employees: nonManagers
            });
          }
        });
      } else {
        // Shared department - group all employees together
        const deptEmployees = employees.filter(emp => emp.department_id === dept.id);
        const manager = deptEmployees.find(emp => emp.id === dept.manager_id);
        const nonManagers = deptEmployees.filter(emp => emp.id !== dept.manager_id);
        
        deptNodes.push({
          ...dept,
          manager: manager || null,
          employees: nonManagers,
          isSplit: false
        });
      }
    });

    // Find employees without departments
    const unassigned = employees.filter(emp => !emp.department_id);
    
    // Find admins (they sit at the top)
    const admins = employees.filter(emp => emp.role === 'admin');

    // Sort departments alphabetically by name
    const sortedDepts = deptNodes.sort((a, b) => a.name.localeCompare(b.name));

    return {
      admins,
      departments: sortedDepts,
      unassigned
    };
  }, [employees, departments, locations, locationsEnabled]);

  // SEARCH HIGHLIGHTS, IT DOESN'T PRUNE.
  //
  // Filtering a hierarchy down to the matches destroys the thing you came to look at —
  // you lose the very lines you were trying to trace. So a search marks people instead:
  // everything stays put, matches get a ring, and the first one is scrolled to.
  const matchIds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return new Set();
    return new Set(
      employees
        .filter((e) => `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase().includes(q)
          || (e.email || '').toLowerCase().includes(q)
          || (e.job_title || '').toLowerCase().includes(q))
        .map((e) => e.id),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, employees]);

  // A match hiding inside a collapsed location may as well not exist, so a search opens
  // everything up. Cheap at this scale and predictable, which beats clever.
  useEffect(() => {
    if (matchIds.size) setCollapsedIds(new Set());
  }, [matchIds]);

  // Chart view respects the Department / Location filters.
  const treeEmployees = useMemo(() => {
    return employees.filter((emp) => {
      if (filterDepartment !== 'all' && emp.department_id !== filterDepartment) return false;
      if (filterLocation !== 'all' && emp.location_id !== filterLocation) return false;
      return true;
    });
  }, [employees, filterDepartment, filterLocation]);

  // Filter employees based on search and filters

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    setCollapsedIds(new Set());
  };

  const collapseAll = () => {
    setCollapsedIds(new Set(getManagerIds(treeEmployees, departments)));
  };

  const didMountFilters = useRef(false);
  useEffect(() => {
    // Expand the tree whenever a Department/Location filter changes so the subset is fully visible.
    if (!didMountFilters.current) { didMountFilters.current = true; return; }
    setCollapsedIds(new Set());
  }, [filterDepartment, filterLocation]);

  const toggleCollapse = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Employee Card Component
  const EmployeeCard = ({ employee, isManager: empIsManager, showExpanded = false }) => {
    const getDeptName = () => departments.find(d => d.id === employee.department_id)?.name || '-';
    const location = locations.find(b => b.id === employee.location_id);
    const getLocationName = () => location?.name || '-';

    // Role badge: one colour set for the whole app (lib/roleBadges, C34).
    const getRoleBadge = () => ({ cls: roleBadgeClass(employee.role), label: roleLabel(employee.role) });
    const roleBadge = getRoleBadge();

    return (
      <div 
        className={`p-3 rounded-lg border transition-all hover:shadow-md cursor-pointer ${
          empIsManager ? 'bg-primary/10 border-primary/30' : 'bg-card border-border'
        }`}
        onClick={() => navigate(`/leave/user/${employee.id}`)}
        data-testid={`org-card-${employee.id}`}
      >
        <div className="flex items-center gap-3">
          {employee.profile_picture_url ? (
            <img 
              src={employee.profile_picture_url} 
              alt={`${employee.first_name} ${employee.last_name}`}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
              roleAvatarClass(employee.role)
            }`}>
              {employee.first_name?.[0]}{employee.last_name?.[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium truncate">
                {employee.first_name} {employee.last_name}
              </p>
              {/* Role badge */}
              <span className={`px-1.5 py-0.5 rounded text-xs ${roleBadge.cls}`}>
                {roleBadge.label}
              </span>
              {/* Dept manager indicator */}
              {empIsManager && <span className="text-xs text-primary">(Dept Head)</span>}
            </div>
            <p className="text-sm text-muted-foreground truncate">{employee.job_title || '-'}</p>
            {/* Always show location when locations are enabled */}
            {locationsEnabled && location && (
              <p className="text-xs text-purple-400 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                {location.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {employee.manager_can_view && (
              <span title="Manager can view details" className="p-1 rounded bg-blue-500/20">
                <Eye className="w-3 h-3 text-blue-400" />
              </span>
            )}
            {employee.manager_can_edit && (
              <span title="Manager can edit details" className="p-1 rounded bg-green-500/20">
                <Edit className="w-3 h-3 text-green-400" />
              </span>
            )}
          </div>
        </div>
        
        {showExpanded && (
          <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Mail className="w-3 h-3" />
              <span className="truncate">{employee.email}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Building2 className="w-3 h-3" />
              <span>{getDeptName()}</span>
            </div>
            {locationsEnabled && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="w-3 h-3" />
                <span>{getLocationName()}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-muted-foreground">
              <span className={`px-1.5 py-0.5 rounded text-xs ${roleBadgeClass(employee.role)}`}>
                {roleLabel(employee.role)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Department Node Component for tree view
  const DepartmentNode = ({ dept }) => {
    const isExpanded = expandedNodes.has(`dept-${dept.id}`);
    const hasEmployees = dept.employees.length > 0 || dept.manager;
    
    return (
      <div className="ml-8 border-l-2 border-border/50 pl-4 py-2" data-testid={`dept-node-${dept.id}`}>
        <div 
          className="flex items-center gap-2 cursor-pointer hover:bg-secondary/30 p-2 rounded-lg transition-colors"
          onClick={() => toggleNode(`dept-${dept.id}`)}
        >
          {hasEmployees ? (
            isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />
          ) : (
            <div className="w-4" />
          )}
          <Building2 className="w-5 h-5 text-primary" />
          <span className="font-medium">{dept.name}</span>
          <span className="text-sm text-muted-foreground">
            ({(dept.employees.length + (dept.manager ? 1 : 0))} {dept.employees.length + (dept.manager ? 1 : 0) === 1 ? 'person' : 'people'})
          </span>
        </div>
        
        {isExpanded && hasEmployees && (
          <div className="ml-6 mt-2 space-y-2">
            {/* Department Manager first */}
            {dept.manager && (
              <EmployeeCard employee={dept.manager} isManager={true} />
            )}
            {/* Then other employees */}
            {dept.employees.map(emp => (
              <EmployeeCard key={emp.id} employee={emp} isManager={false} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Chart View Component
  const ChartView = () => (
    <div className="space-y-6" data-testid="org-chart-view">
      {/* Company Level - Admins */}
      {orgStructure.admins.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-purple-400" />
            <h3 className="font-heading font-semibold">Leadership / Administrators</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {orgStructure.admins.map(admin => (
              <EmployeeCard key={admin.id} employee={admin} isManager={false} showExpanded />
            ))}
          </div>
        </div>
      )}

      {/* Departments */}
      {orgStructure.departments.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Departments</h3>
          </div>
          <div className="space-y-1">
            {orgStructure.departments.map(dept => (
              <DepartmentNode key={dept.id} dept={dept} />
            ))}
          </div>
        </div>
      )}

      {/* Unassigned Employees */}
      {orgStructure.unassigned.filter(e => e.role !== 'admin').length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-heading font-semibold">Unassigned Employees</h3>
            <span className="text-sm text-muted-foreground">
              ({orgStructure.unassigned.filter(e => e.role !== 'admin').length})
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {orgStructure.unassigned.filter(e => e.role !== 'admin').map(emp => (
              <EmployeeCard key={emp.id} employee={emp} isManager={false} showExpanded />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // The TableView component was removed on 30/07/2026 along with the Chart/Table
  // switch: the Employees page already is that list. `filteredEmployees` went with
  // it — the chart filters via `treeEmployees` and highlights via `matchIds`.


  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading text-2xl font-bold">Organisation Chart</h1>
                <p className="text-sm text-muted-foreground">View your company structure and reporting lines</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-6">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* The Chart/Table switch was removed on 30/07/2026 (Rob's call): this page
                is the ORG CHART, and the same people as a table is what the Employees
                page already is. Two routes to one list is a maintenance tax with no
                benefit. `viewMode` is kept as a constant so the rest of the page reads
                unchanged; TableView went with it. */}

            {/* Expand/Collapse All - Only show in Chart view */}
            {viewMode === 'chart' && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={expandAll}
                  data-testid="expand-all-btn"
                >
                  <ChevronsDownUp className="w-4 h-4 mr-2" />
                  Expand All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={collapseAll}
                  data-testid="collapse-all-btn"
                >
                  <ChevronsUpDown className="w-4 h-4 mr-2" />
                  Collapse All
                </Button>
              </div>
            )}

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Department Filter */}
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-[180px]" data-testid="filter-department">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Location Filter (if enabled) */}
            {locationsEnabled && locations.length > 0 && (
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="w-[180px]" data-testid="filter-location">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Searching narrows the chart just as much as a filter does — you're left
                with everything dimmed and one card lit — so it belongs behind the same
                escape hatch. Before, a search could only be undone by hunting back to
                the box and deleting the text. */}
            {(filterDepartment !== 'all' || filterLocation !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterDepartment('all'); setFilterLocation('all'); setSearchQuery(''); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1.5" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Stats — click a card to see the breakdown behind the total */}
          {(() => {
            const managersList = employees.filter(e => e.role === 'manager' || e.role === 'admin');
            const deptName = (id) => departments.find(d => d.id === id)?.name || '\u2014';
            const Avatar = ({ e }) => (
              e.profile_picture_url
                ? <img src={e.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                : <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-[10px] font-bold flex-shrink-0">{e.first_name?.[0]}{e.last_name?.[0]}</div>
            );
            const PersonBtn = ({ e, sub }) => (
              <button type="button" onClick={() => navigate(`/leave/user/${e.id}`)} className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/50 text-left">
                <Avatar e={e} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{e.first_name} {e.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{sub}</p>
                </div>
              </button>
            );
            const stats = [
              { id: 'employees', label: 'Total Employees', value: employees.length, color: 'text-primary' },
              { id: 'departments', label: 'Departments', value: departments.length, color: 'text-blue-500' },
              { id: 'managers', label: 'Managers', value: managersList.length, color: 'text-green-500' },
              ...(locationsEnabled ? [{ id: 'locations', label: 'Locations', value: locations.length, color: 'text-purple-500' }] : []),
            ];
            return (
              <div className="mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stats.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setOpenStat(openStat === s.id ? null : s.id)}
                      className={`text-left bg-card rounded-xl border p-4 transition-colors hover:border-primary/50 ${openStat === s.id ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">{s.label}</p>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${openStat === s.id ? 'rotate-180' : ''}`} />
                      </div>
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    </button>
                  ))}
                </div>

                {openStat && (
                  <div className="mt-3 bg-card rounded-xl border border-border p-4">
                    {openStat === 'employees' && (
                      <>
                        <p className="text-sm font-semibold mb-3">All employees ({employees.length})</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-72 overflow-y-auto">
                          {[...employees].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')).map((e) => (
                            <PersonBtn key={e.id} e={e} sub={e.job_title || deptName(e.department_id)} />
                          ))}
                        </div>
                      </>
                    )}
                    {openStat === 'departments' && (
                      <>
                        <p className="text-sm font-semibold mb-3">Departments ({departments.length})</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {departments.map((d) => {
                            const count = employees.filter((e) => e.department_id === d.id).length;
                            return (
                              <button key={d.id} type="button" onClick={() => { setFilterDepartment(d.id); setOpenStat(null); }} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border/60 hover:border-primary/50 hover:bg-secondary/40 text-left">
                                <span className="text-sm font-medium truncate">{d.name}</span>
                                <span className="text-xs text-muted-foreground flex-shrink-0">{count} {count === 1 ? 'person' : 'people'}</span>
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Tip: click a department to filter the chart.</p>
                      </>
                    )}
                    {openStat === 'managers' && (
                      <>
                        <p className="text-sm font-semibold mb-3">Managers &amp; admins ({managersList.length})</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                          {managersList.map((e) => (
                            <PersonBtn key={e.id} e={e} sub={`${e.role} \u2022 ${deptName(e.department_id)}`} />
                          ))}
                        </div>
                      </>
                    )}
                    {openStat === 'locations' && (
                      <>
                        <p className="text-sm font-semibold mb-3">Locations ({locations.length})</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {locations.map((b) => {
                            const count = employees.filter((e) => e.location_id === b.id).length;
                            return (
                              <button key={b.id} type="button" onClick={() => { setFilterLocation(b.id); setOpenStat(null); }} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border/60 hover:border-primary/50 hover:bg-secondary/40 text-left">
                                <span className="text-sm font-medium truncate flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />{b.name}</span>
                                <span className="text-xs text-muted-foreground flex-shrink-0">{count} {count === 1 ? 'person' : 'people'}</span>
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Tip: click a location to filter the chart.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            /* View Content */
            viewMode === 'chart' ? (
              <>
                {/* Same chart, two lenses — a toggle rather than a second page, because
                    two pages could silently disagree. Approvals leads: this is holiday
                    software, so "who signs this off" is the question people arrive with. */}
                {/* STACKED, both hard left (Rob, 30/07/2026): lens toggle first, key
                    directly beneath it. Reading order matches the job — pick your view,
                    then learn what the lines mean. Pinned to the far right, the key was
                    easy to miss entirely on a wide screen. */}
                <div className="flex flex-col items-start gap-2 mb-4 px-4">
                  <div className="inline-flex rounded-lg border border-border overflow-hidden">
                    {[
                      { v: LENS.APPROVALS, label: 'Who approves whom' },
                      { v: LENS.REPORTING, label: 'Reporting lines' },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setLens(o.v)}
                        aria-pressed={lens === o.v}
                        className={`px-3 py-1.5 text-sm transition-colors ${
                          lens === o.v
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid={`org-lens-${o.v}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  {/* Line samples use the SAME classes as the real connectors —
                      a key drawn in different greys to the thing it explains is
                      worse than no key at all. */}
                  <div className="flex items-center gap-5 rounded-lg border border-border bg-secondary/40 px-3 py-1.5">
                    <span className="flex items-center gap-2 text-sm">
                      <svg width="26" height="8" aria-hidden="true" className="flex-shrink-0">
                        <line x1="0" y1="4" x2="26" y2="4" className="orgtree-link" />
                      </svg>
                      <span className="text-foreground">you set this</span>
                    </span>
                    <span className="flex items-center gap-2 text-sm">
                      <svg width="26" height="8" aria-hidden="true" className="flex-shrink-0">
                        <line x1="0" y1="4" x2="26" y2="4" className="orgtree-link orgtree-link-inferred" />
                      </svg>
                      <span className="text-foreground">we worked it out</span>
                    </span>
                  </div>
                </div>
                <OrgTree
                  employees={treeEmployees}
                  departments={departments}
                  locations={locations}
                  locationsEnabled={locationsEnabled}
                  collapsedIds={collapsedIds}
                  approverChains={approverChains}
                  lens={lens}
                  highlightIds={matchIds}
                  onToggle={toggleCollapse}
                  onOpen={(id, opts) => {
                    const emp = employees.find((e) => e.id === id);
                    if (!emp) return;
                    setEditHighlight(!!(opts && opts.needsApproval));
                    setEditing(emp);
                  }}
                />
              </>
            ) : null
          )}
        </div>
      </main>

      {/* THE SAME dialog the Employees page uses — same component, same props, so any
          change to it lands in both places. Opened on Permissions because that's the
          tab you want when you've just clicked someone whose routing looks wrong. */}
      <AddEditEmployeeDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); setEditHighlight(false); } }}
        employee={editing}
        departments={departments}
        people={employees}
        locations={locations}
        locationsEnabled={locationsEnabled}
        initialTab="permissions"
        highlightApproval={editHighlight}
        isAdmin={isAdmin}
        viewOnly={!isAdmin}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default OrgChartPage;
